import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { linkPreviewsEnabled } from './linkPreviews';
import { createPortal } from 'react-dom';
import { MessageAttachments, type Attachment } from '../../core/messageAttachments';
import type { MessageListRow } from '../../core/messageListDerivation';
import { MessageRowLayout } from '../../core/messageRowLayout';
import { MessagePriority } from '../../core/messagePriority';
import { PostEmbed, type PostEmbed as PostEmbedValue } from '../../core/postEmbed';
import { CoreLinkCards, type WebCard } from '../../core/coreLinkCards';
import { useCoreCard } from '../features/coreCards/coreCards';
import { CoreCardView } from '../features/coreCards/CoreCardView';
import { mediaBase } from '../media';
import type { ReactionChip } from '../../core/reactionsDerivation';
import { PostState, type PostFileRecord, type PostRecord } from '../../core/records';
import { SystemMessageDerivation, type SystemNotice } from '../../core/systemMessageDerivation';
import type { ThreadFooter } from '../../core/threadFooterDerivation';
import { Icon } from '../../design/icons';
import type { EditRights } from '../../core/messageEditRights';
import { t, tf } from '../../i18n/i18n';
import { Avatar } from '../Avatar';
import type { SeenLevel } from '../models/conversationModel';
import { MessageBody, type BodyContext } from './MessageBody';
import { PostFiles } from '../features/attachments/PostFiles';
import { RelativeTime } from '../../core/relativeTime';
import { EmojiGlyph } from '../features/emoji/EmojiGlyph';
import { ReactionChips, useReactionLayer } from '../features/emoji/ReactionChips';
import { requestEdit } from '../features/compose/composeSignals';
import { PermalinkCard } from '../features/compose/MessageLinks';
import { PriorityPill } from '../features/compose/Priority';
import { imageUnder, type ImageRef } from '../features/attachments/imageClipboard';
import { tableUnder, type TableRef } from '../features/extras/tableCopy';
import { AcknowledgementMenuItem, LinkMenuItems, linkUnder, TableMenuItems, ThreadWindowMenuItem } from '../features/extras/rowMenu';
import { isRemoteSupportPost } from '../../core/remoteSupportPost';
import { RemoteSupportPostRow } from '../features/remoteSupport/RemoteSupportSurfaces';
import { CallInvitePostRow, isCallInvitePost } from '../features/calls/CallInvite';
import { count } from '../perf';
import { AckBar, ForwardedMarker, PinnedMarker, SeenOpener } from '../features/threads/rowExtras';
import { isForwarded } from '../../core/forwardPresentation';
import { ReportMenuItem } from '../features/moderation/Moderation';
import type { AckState } from '../../data/features/threads';
import { nameColor } from '../identityColors';

export interface RowActions {
  onReact(postId: string, emojiName: string): void;
  onOpenThread(rootId: string): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onRetry(postId: string): void;
  onDiscard(postId: string): void;
  onAction(postId: string, actionId: string, cookie: string | null, selectedOption: string | null): void;
  onCopyText(text: string): void;
  onCopyLink(postId: string): void;
  onPin(postId: string, pinned: boolean): void;
  onSave(postId: string, saved: boolean): void;
  onMarkUnread(postId: string): void;
  onEdit(postId: string, message: string): Promise<boolean>;
  onDelete(postId: string): void;
  /** Reenviar (features/compose): opens the forward dialog for this post. */
  onForward?(post: PostRecord, authorName: string): void;
  /** «Copiar imagen» / «Guardar imagen como…» on a picture (features/attachments/imageClipboard). */
  onCopyImage?(image: ImageRef): void;
  onSaveImage?(image: ImageRef): void;
  quickReactions: string[];
}

export interface RowProps {
  row: MessageListRow;
  authorName: string;
  authorVersion: number;
  /** The author is a bot ACCOUNT (`users.is_bot`, PARITY P305) — the «BOT» tag like a webhook's. */
  authorIsBot?: boolean;
  presence: string | null;
  isOwn: boolean;
  /** Edit / Delete in the menu — own messages, or someone else's for an admin (`core/messageEditRights`). */
  editRights: EditRights;
  files: PostFileRecord[] | undefined;
  chips: ReactionChip[] | undefined;
  footer: ThreadFooter | undefined;
  footerNames: (id: string) => { name: string; version: number };
  seen: SeenLevel;
  saved?: boolean;
  marked?: boolean;
  ctx: BodyContext;
  actions: RowActions;
  now: number;
  inThread?: boolean;
  /** §41.3: the post's acknowledgement request / confirmations (features/threads). */
  ack?: AckState;
  /** The reader's id (the ack bar: «Confirmar» vs «Confirmado»). */
  me?: string;
}

function noticeText(n: SystemNotice): string {
  // actor/subject already arrive in @handle form (SystemMessageDerivation).
  const at = (s: string) => s;
  switch (n.kind) {
    case 'joined':
      return tf('channel.system.joined', at(n.actor));
    case 'left':
      return tf('channel.system.left', at(n.actor));
    case 'added':
      return tf('channel.system.added', at(n.actor), at(n.subject));
    case 'removed':
      return tf('channel.system.removed', at(n.subject));
    case 'headerSet':
      return tf('channel.system.headerSet', at(n.actor), n.detail);
    case 'headerCleared':
      return tf('channel.system.headerCleared', at(n.actor));
    case 'purposeSet':
      return tf('channel.system.purposeSet', at(n.actor), n.detail);
    case 'purposeCleared':
      return tf('channel.system.purposeCleared', at(n.actor));
    case 'renamed':
      return tf('channel.system.renamed', at(n.actor), n.detail);
    case 'archived':
      return n.actor ? tf('channel.system.archived', at(n.actor)) : t('channel.system.archivedGeneric');
    case 'restored':
      return n.actor ? tf('channel.system.restored', at(n.actor)) : t('channel.system.restoredGeneric');
    default:
      return n.detail;
  }
}

export const MessageRow = memo(function MessageRow(p: RowProps) {
  count('row.render');
  const { row } = p;
  const post = row.post;
  const separators = (
    <>
      {row.dayLabel ? (
        <div className="day-separator" role="separator">
          <span>{row.dayLabel}</span>
        </div>
      ) : null}
      {row.showsUnreadSeparator ? (
        <div className="unread-separator" role="separator">
          <span>{t('channel.unreadSeparator')}</span>
        </div>
      ) : null}
    </>
  );

  if (isRemoteSupportPost(post.type)) {
    return (
      <>
        {separators}
        <RemoteSupportPostRow post={post} nameOf={(id) => p.footerNames(id).name} />
      </>
    );
  }

  // «Invitar» markers (shared/API.md §30.3): the invite DM and the move post draw as call rows.
  if (!post.type && isCallInvitePost(post.props)) {
    return (
      <>
        {separators}
        <CallInvitePostRow post={post} authorName={p.authorName} />
      </>
    );
  }

  if (SystemMessageDerivation.isSystem(post.type)) {
    const notice = SystemMessageDerivation.notice(post.type, post.props, post.message);
    return (
      <>
        {separators}
        <div className="system-row">{notice ? noticeText(notice) : post.message}</div>
      </>
    );
  }

  const props = MessageAttachments.postProps(post.props);
  const isWebhook = props.isFromWebhook;
  const isEphemeral = SystemMessageDerivation.isEphemeral(post.type);
  const hasText = post.message.trim() !== '';
  const embed = post.embedJson ? PostEmbed.fromJson(post.embedJson) : null;
  const pending = post.state === PostState.Pending;
  const failed = post.state === PostState.Failed;
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  // The picture the right-click landed on: its items lead the menu (the browser's own image menu
  // never shows here — this row takes the right-click).
  const [menuImage, setMenuImage] = useState<ImageRef | null>(null);
  // …and the table it landed on («Copiar tabla» leads the menu, PARITY P313).
  const [menuTable, setMenuTable] = useState<TableRef | null>(null);
  const [menuLink, setMenuLink] = useState<string | null>(null);
  // The full picker (hover bar, menu, "+" chip) and the who-reacted dialog (chip right-click).
  const reactionUi = useReactionLayer(post.id, (name) => p.actions.onReact(post.id, name), p.ctx.customEmoji);
  const myReactions = new Set(p.chips?.filter((c) => c.mine).map((c) => c.emojiName));
  const openAuthor = (e: React.MouseEvent) => {
    if (isWebhook) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    p.actions.onOpenUserId(post.userId, { x: r.left, y: r.bottom + 6 });
  };

  // WhatsApp-style rows (VISUAL-DESIGN §4, 2026-10-13): side / avatar / name / bubble / caret.
  const layout = MessageRowLayout.presentation({ isOwn: p.isOwn, isGroupStart: row.isGroupStart, hasText });
  const webhook = isWebhook ? { iconUrl: props.webhookIconUrl, username: props.webhookUsername } : null;
  const hasPriority = !!MessagePriority.pillKey(post.priority);
  // The name line: the first line of the run's first bubble (or a small label above a message
  // without text). The BOT tag and the priority pill ride on it; a message that shows no name
  // still keeps its priority pill on a line of its own.
  const nameLine =
    layout.showsName || hasPriority ? (
      <div className="msg-name">
        {layout.showsName ? (
          <button className="author" style={{ color: nameColor(MessageRowLayout.nameColor(post.userId, webhook)) }} onClick={openAuthor}>
            {p.authorName}
          </button>
        ) : null}
        {layout.showsName && (isWebhook || p.authorIsBot) ? <span className="bot-tag">{t('channel.botBadge')}</span> : null}
        <PriorityPill priority={post.priority} />
      </div>
    ) : null;
  // The time on every message ("(editado)" before it, own seen checks after it); hover = the full
  // timestamp, with the workspace's clock when the reader is on another one.
  const meta = (
    <span className="msg-meta">
      {post.editAt > 0 ? <span className="edited">{t('channel.message.edited')}</span> : null}
      <time dateTime={new Date(post.createAt).toISOString()} title={MessageRowLayout.fullTimestamp(post.createAt)}>
        {MessageRowLayout.shortTime(post.createAt)}
      </time>
      {p.seen ? (
        p.isOwn && p.seen !== 'sending' && post.state === PostState.Synced ? (
          <SeenOpener postId={post.id}>
            <SeenChecks level={p.seen} />
          </SeenOpener>
        ) : (
          <SeenChecks level={p.seen} />
        )
      ) : null}
    </span>
  );
  const bubbleless = !layout.hasBubble;

  return (
    <>
      {separators}
      <article
        className={`msg${layout.side === 'trailing' ? ' own' : ''}${row.isGroupStart ? ' group-start' : ''}${p.marked ? ' jump-mark' : ''}${menuAt || reactionUi.open ? ' menu-open' : ''}`}
        data-post-id={post.id}
        aria-label={p.authorName}
        onContextMenu={(e) => {
          if (post.state !== PostState.Synced || isEphemeral) return;
          e.preventDefault();
          setMenuImage(imageUnder(e.target));
          setMenuTable(tableUnder(e.target));
          setMenuLink(linkUnder(e.target));
          setMenuAt({ x: e.clientX, y: e.clientY });
        }}
      >
        <div className="gutter">
          {layout.showsAvatar ? (
            <button className="avatar-button" onClick={openAuthor} tabIndex={-1} aria-hidden="true">
              <Avatar
                userId={post.userId}
                name={p.authorName}
                size={32}
                version={p.authorVersion}
                presence={isWebhook ? null : p.presence}
                imageUrl={props.webhookIconUrl || null}
                colorKey={webhook ? MessageRowLayout.identityKey(post.userId, webhook) : undefined}
                ring={!isWebhook}
              />
            </button>
          ) : null}
        </div>
        <div className="content">
          {bubbleless && nameLine ? <div className="msg-label">{nameLine}</div> : null}
          {post.isPinned ? <PinnedMarker /> : null}
          {isForwarded(post.props) ? <ForwardedMarker /> : null}
          {layout.hasBubble ? (
            <div className={`bubble${p.isOwn ? ' own' : ''}${layout.caret ? ' caret' : ''}${pending ? ' pending' : ''}`}>
              {nameLine}
              <div className="bubble-flow">
                <div className="bubble-text">
                  <MessageBody message={post.message} ctx={p.ctx} />
                </div>
                {meta}
              </div>
            </div>
          ) : null}
          {isEphemeral ? <span className="ephemeral-note">{t('channel.message.onlyVisibleToYou')}</span> : null}
          {props.attachments
            .filter((a) => !MessageAttachments.isEmpty(a))
            .map((a, i) => (
              <AttachmentCard key={i} attachment={a} ctx={p.ctx} onAction={(actionId, cookie, option) => p.actions.onAction(post.id, actionId, cookie, option)} />
            ))}
          <PostFiles files={p.files} postId={post.id} author={p.authorName} at={post.createAt} />
          <PreviewSlot
            message={post.message}
            embed={embed}
            suppressed={props.attachments.some((a) => !MessageAttachments.isEmpty(a))}
            fallback={
              embed?.type === PostEmbed.typeImage ? (
                linkPreviewsEnabled() && !PostEmbed.pictureIsAttached(embed.url, p.files?.map((f) => f.fileId) ?? []) ? <PicturePreviewCard embed={embed} /> : null
              ) : embed && PostEmbed.isDrawable(embed) ? (
                embed.type === PostEmbed.typePermalink ? <PermalinkCard embed={embed} /> : linkPreviewsEnabled() ? <EmbedCard embed={embed} /> : null
              ) : null
            }
          />
          {bubbleless ? <div className={`msg-label time${pending ? ' pending' : ''}`}>{meta}</div> : null}
          {failed ? (
            <div className="failed-line" role="alert">
              <Icon name="alertCircle" size={14} />
              {t('channel.error.sendMessage')}
              <button onClick={() => p.actions.onRetry(post.id)}>{t('common.retry')}</button>
              <button onClick={() => p.actions.onDiscard(post.id)}>{t('channel.messageDiscard')}</button>
            </div>
          ) : null}
          {p.chips?.length ? (
            <ReactionChips
              chips={p.chips}
              custom={p.ctx.customEmoji}
              onToggle={(name) => p.actions.onReact(post.id, name)}
              onAdd={reactionUi.openPicker}
              onDetails={reactionUi.openDetails}
            />
          ) : null}
          {p.ack ? <AckBar postId={post.id} state={p.ack} me={p.me ?? ''} isOwn={p.isOwn} now={p.now} /> : null}
          {p.footer && !p.inThread ? <Footer footer={p.footer} names={p.footerNames} now={p.now} onOpen={() => p.actions.onOpenThread(p.footer!.rootId)} /> : null}
        </div>
        {post.state === PostState.Synced && !isEphemeral ? (
          <div className="hover-bar" role="toolbar">
            {p.actions.quickReactions.slice(0, 3).map((name) => (
              <button key={name} className={`quick${myReactions.has(name) ? ' mine' : ''}`} title={`:${name}:`} onClick={() => p.actions.onReact(post.id, name)}>
                <EmojiGlyph name={name} custom={p.ctx.customEmoji} />
              </button>
            ))}
            {!p.inThread ? (
              <button title={t('channel.replyInThread')} onClick={() => p.actions.onOpenThread(post.rootId || post.id)}>
                <Icon name="reply" size={16} />
              </button>
            ) : null}
            {/* «Reenviar» beside reply, its arrow the mirror of reply's (user, 2026-10-28). */}
            {p.actions.onForward ? (
              <button title={t('forward.title')} aria-label={t('forward.title')} onClick={() => p.actions.onForward?.(post, p.authorName)}>
                <Icon name="forward" size={16} />
              </button>
            ) : null}
            <button
              data-emoji-opener=""
              title={t('channel.addReaction')}
              aria-label={t('channel.addReaction')}
              onClick={(e) => reactionUi.openPicker(e.currentTarget.getBoundingClientRect())}
            >
              <Icon name="smilePlus" size={17} />
            </button>
            <button
              title={t('web.msg.more')}
              aria-label={t('web.msg.more')}
              aria-haspopup="menu"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setMenuImage(null);
                setMenuTable(null);
                setMenuLink(null);
                setMenuAt({ x: r.right - 220, y: r.bottom + 4 });
              }}
            >
              <Icon name="more" size={18} strokeWidth={2.4} />
            </button>
          </div>
        ) : null}
        {menuAt ? (
          <MessageMenu
            at={menuAt}
            image={menuImage}
            extras={{
              lead: (
                <>
                  <LinkMenuItems url={menuLink} onDone={() => setMenuAt(null)} />
                  <TableMenuItems table={menuTable} onDone={() => setMenuAt(null)} />
                </>
              ),
              afterReply: <ThreadWindowMenuItem post={post} onDone={() => setMenuAt(null)} />,
              afterForward: (
                <>
                  <AcknowledgementMenuItem post={post} onDone={() => setMenuAt(null)} />
                  <ReportMenuItem post={post} authorName={p.authorName} onDone={() => setMenuAt(null)} />
                </>
              ),
            }}
            canCopyText={post.message.trim() !== ''}
            rights={p.editRights}
            pinned={post.isPinned}
            saved={!!p.saved}
            inThread={!!p.inThread}
            quick={p.actions.quickReactions}
            mine={myReactions}
            hasReactions={!!p.chips?.length}
            ctx={p.ctx}
            onClose={() => setMenuAt(null)}
            onPick={(action) => {
              setMenuAt(null);
              switch (action.kind) {
                case 'copyImage':
                  return menuImage && p.actions.onCopyImage?.(menuImage);
                case 'saveImage':
                  return menuImage && p.actions.onSaveImage?.(menuImage);
                case 'react':
                  return p.actions.onReact(post.id, action.name);
                case 'addReaction':
                  return reactionUi.openPicker({ left: menuAt.x, right: menuAt.x + 230, top: menuAt.y, bottom: menuAt.y });
                case 'reactions':
                  return reactionUi.openDetails();
                case 'reply':
                  return p.actions.onOpenThread(post.rootId || post.id);
                case 'forward':
                  return p.actions.onForward?.(post, p.authorName);
                case 'copy':
                  return p.actions.onCopyText(post.message);
                case 'link':
                  return p.actions.onCopyLink(post.id);
                case 'pin':
                  return p.actions.onPin(post.id, !post.isPinned);
                case 'save':
                  return p.actions.onSave(post.id, !p.saved);
                case 'unread':
                  return p.actions.onMarkUnread(post.id);
                case 'edit':
                  // §42: the conversation's own composer takes the message (features/compose).
                  return requestEdit(post.id, post.channelId, !!p.inThread, null);
                case 'delete':
                  return p.actions.onDelete(post.id);
              }
            }}
          />
        ) : null}
      </article>
      {reactionUi.layer}
    </>
  );
});

type MenuAction =
  | { kind: 'react'; name: string }
  | { kind: 'addReaction' | 'reactions' }
  | { kind: 'reply' | 'copy' | 'link' | 'pin' | 'save' | 'unread' | 'edit' | 'delete' | 'forward' | 'copyImage' | 'saveImage' };

/** The message menu (hover "…" or right-click): the Mac's full context menu, in its order. */
function MessageMenu({ at, image, extras, canCopyText, rights, pinned, saved, inThread, quick, mine, hasReactions, ctx, onClose, onPick }: { at: { x: number; y: number }; image: ImageRef | null; extras?: { lead?: React.ReactNode; afterReply?: React.ReactNode; afterForward?: React.ReactNode }; canCopyText: boolean; rights: EditRights; pinned: boolean; saved: boolean; inThread: boolean; quick: string[]; mine: Set<string>; hasReactions: boolean; ctx: BodyContext; onClose(): void; onPick(a: MenuAction): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pos, setPos] = useState(at);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: Math.max(8, Math.min(at.x, window.innerWidth - r.width - 8)), y: Math.max(8, Math.min(at.y, window.innerHeight - r.height - 8)) });
  }, [at]);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    // Armed a tick later (the opening click must not close it) — and DISARMED on cleanup: a menu
    // closed within that tick otherwise leaked this listener, which then closed the NEXT menu on the
    // mousedown over its own item, so the item's click never fired.
    const arm = setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', esc);
    window.addEventListener('blur', onClose);
    return () => {
      clearTimeout(arm);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);
  // A PORTAL: rows use `content-visibility: auto`, whose paint containment clips anything inside a
  // row — a fixed menu rendered there was invisible.
  return createPortal(
    <div className="popover message-menu" role="menu" ref={ref} style={{ position: 'fixed', left: pos.x, top: pos.y, width: 230 }}>
      {extras?.lead}
      {image ? (
        <>
          <button className="menu-item" role="menuitem" data-menu="copy-image" onClick={() => onPick({ kind: 'copyImage' })}>
            <Icon name="copy" size={16} /> {t('web.image.copy')}
          </button>
          <button className="menu-item" role="menuitem" data-menu="save-image" onClick={() => onPick({ kind: 'saveImage' })}>
            <Icon name="download" size={16} /> {t('web.image.saveAs')}
          </button>
          <div className="menu-sep" />
        </>
      ) : null}
      <div className="menu-quick">
        {quick.slice(0, 6).map((name) => (
          <button key={name} className={mine.has(name) ? 'mine' : undefined} title={`:${name}:`} onClick={() => onPick({ kind: 'react', name })}>
            <EmojiGlyph name={name} custom={ctx.customEmoji} />
          </button>
        ))}
      </div>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'addReaction' })}>
        <Icon name="smilePlus" size={16} /> {t('channel.addReaction')}…
      </button>
      <div className="menu-sep" />
      {!inThread ? (
        <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'reply' })}>
          <Icon name="reply" size={16} /> {t('channel.replyInThread')}
        </button>
      ) : null}
      {extras?.afterReply}
      {hasReactions ? (
        <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'reactions' })}>
          <Icon name="smile" size={16} /> {t('web.emoji.reactions.viewAll')}
        </button>
      ) : null}
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'forward' })}>
        <Icon name="forward" size={16} /> {t('forward.title')}
      </button>
      <div className="menu-sep" />
      {extras?.afterForward}
      {/* «Copiar texto» only when there is text: on a picture-only post it copied nothing, and beside
          «Copiar imagen» it read as the picture's copy (user, 2026-10-16) — Android's rule. */}
      {canCopyText ? (
        <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'copy' })}>
          <Icon name="copy" size={16} /> {t('channel.copyMessage')}
        </button>
      ) : null}
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'link' })}>
        <Icon name="link" size={16} /> {t('channel.action.copyLink')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'pin' })}>
        <Icon name="pin" size={16} /> {pinned ? t('channel.unpin') : t('channel.pin')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'save' })}>
        <Icon name="bookmark" size={16} /> {saved ? t('channel.unsave') : t('channel.save')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'unread' })}>
        <Icon name="envelope" size={16} /> {t('channel.markUnread')}
      </button>
      {rights.canEdit || rights.canDelete ? (
        <>
          <div className="menu-sep" />
          {rights.canEdit ? (
            <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'edit' })}>
              <Icon name="pencilLine" size={16} /> {t('channel.editMessage')}
            </button>
          ) : null}
          {rights.canDelete ? (
            <button
              className="menu-item danger"
              role="menuitem"
              onClick={() => (confirmDelete ? onPick({ kind: 'delete' }) : setConfirmDelete(true))}
            >
              <Icon name="trash" size={16} /> {confirmDelete ? t('channel.deleteConfirmTitle') : t('channel.deleteMessage')}
            </button>
          ) : null}
        </>
      ) : null}
    </div>,
    document.body,
  );
}

function SeenChecks({ level }: { level: Exclude<SeenLevel, null> }) {
  // A clock = still on its way; one muted check = on the server, unopened; two muted = some; two
  // accented = all.
  if (level === 'sending') {
    return (
      <span className="checks sending" title={t('channel.undeliveredRetrying')} aria-label={t('channel.undeliveredRetrying')}>
        <Icon name="clock" size={13} strokeWidth={1.8} />
      </span>
    );
  }
  return (
    <span className={`checks${level === 'all' ? ' all' : ''}`} title={level === 'all' ? t('channel.seen.byAll') : undefined}>
      <Icon name={level === 'sent' ? 'check' : 'checkDouble'} size={15} strokeWidth={1.8} />
    </span>
  );
}

function Footer({ footer, names, now, onOpen }: { footer: ThreadFooter; names: (id: string) => { name: string; version: number }; now: number; onOpen(): void }) {
  const unread = footer.unreadReplies > 0 || footer.unreadMentions > 0;
  return (
    <button className={`thread-footer${unread ? ' unread' : ''}`} onClick={onOpen}>
      <span className="faces">
        {footer.avatarUserIds.map((id) => {
          const n = names(id);
          return <Avatar key={id} userId={id} name={n.name} size={20} version={n.version} />;
        })}
      </span>
      <span className="count">{footer.replyCount === 1 ? t('channel.replyCount.one') : tf('channel.replyCount.many', footer.replyCount)}</span>
      {footer.lastReplyAt ? <span className="last">{RelativeTime.format(footer.lastReplyAt, now, { yesterday: t('channelList.time.yesterday') })}</span> : null}
    </button>
  );
}

/** The card's accent as a CSS colour: Slack's named statuses → theme tokens, hex → itself (MessageAttachments.accent). */
function accentCss(raw: string | null): string | null {
  const a = MessageAttachments.accent(raw);
  if (!a) return null;
  return a.kind === 'token' ? `var(--${a.token})` : a.css;
}

/**
 * An integration's card (brief 2026-10-16 §A — beat the webapp): full-height accent rule, the accent
 * washed over the raised surface, hairline, author line with icon, linked title, thumb at the
 * top-trailing corner, Mattermost's two-per-row short fields (`fieldRows`), image, footer with icon.
 */
function AttachmentCard({ attachment: a, ctx, onAction }: { attachment: Attachment; ctx: BodyContext; onAction(actionId: string, cookie: string | null, option: string | null): void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const accent = accentCss(a.color);
  const rows = useMemo(() => MessageAttachments.fieldRows(a.fields), [a.fields]);
  const authorIcon = MessageAttachments.safeIconUrl(a.authorIcon);
  const footerIcon = MessageAttachments.safeIconUrl(a.footerIcon);
  const thumb = MessageAttachments.safeIconUrl(a.thumbUrl);
  const image = MessageAttachments.safeIconUrl(a.imageUrl);
  const author = a.authorName ? (
    <span className="author-name">{a.authorName}</span>
  ) : null;
  return (
    <div className={`attachment-card${accent ? ' accented' : ''}`} style={accent ? ({ '--card-accent': accent } as CSSProperties) : undefined}>
      <div className="rule" />
      <div className="body">
        {a.pretext ? <MessageBody message={a.pretext} ctx={ctx} /> : null}
        {a.authorName || authorIcon || a.title || a.text || thumb ? (
        <div className="card-main">
          <div className="card-text">
            {a.authorName || authorIcon ? (
              <div className="author">
                {authorIcon ? <img className="author-icon" src={authorIcon} alt="" loading="lazy" decoding="async" /> : null}
                {a.authorLink && author ? (
                  <a href={a.authorLink} target="_blank" rel="noreferrer noopener">
                    {author}
                  </a>
                ) : (
                  author
                )}
              </div>
            ) : null}
            {a.title ? (
              <div className="title">
                {a.titleLink ? (
                  <a href={a.titleLink} target="_blank" rel="noreferrer noopener">
                    {a.title}
                  </a>
                ) : (
                  a.title
                )}
              </div>
            ) : null}
            {a.text ? <MessageBody message={a.text} ctx={ctx} /> : null}
          </div>
          {thumb ? <img className="thumb" src={thumb} alt="" loading="lazy" decoding="async" /> : null}
        </div>
        ) : null}
        {rows.length ? (
          <div className="fields">
            {rows.map((row, i) => (
              <div key={i} className={`field-row${row.length === 2 ? ' pair' : row[0].short ? ' half' : ''}`}>
                {row.map((f, j) => (
                  <div key={j} className="card-field">
                    {f.title ? <div className="field-title">{f.title}</div> : null}
                    {f.value ? <MessageBody message={f.value} ctx={ctx} /> : null}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}
        {image ? <CardImage src={image} /> : null}
        {a.actions.length ? (
          <div className="actions-row">
            {a.actions.map((action) =>
              MessageAttachments.isSelect(action) ? (
                <select
                  key={action.id}
                  className="action-button"
                  defaultValue=""
                  onChange={(e) => e.target.value && onAction(action.id, action.cookie, e.target.value)}
                >
                  <option value="" disabled>
                    {action.name}
                  </option>
                  {action.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.text}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  key={action.id}
                  className={`action-button${action.style === 'primary' || action.style === 'good' || action.style === 'success' ? ' primary' : action.style === 'danger' || action.style === 'warning' ? ' danger' : ''}`}
                  disabled={busy === action.id}
                  onClick={() => {
                    setBusy(action.id);
                    onAction(action.id, action.cookie, null);
                    setTimeout(() => setBusy(null), 1500);
                  }}
                >
                  {action.name}
                </button>
              ),
            )}
          </div>
        ) : null}
        {a.footer || footerIcon ? (
          <div className="card-footer">
            {footerIcon ? <img className="footer-icon" src={footerIcon} alt="" loading="lazy" decoding="async" /> : null}
            {a.footer ? <span>{a.footer}</span> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** `image_url`: up to 280×220, a sunken placeholder until decoded (the browser's HTTP cache is the image cache). */
function CardImage({ src }: { src: string }) {
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  if (state === 'failed') return null;
  return (
    <a className={`card-image ${state}`} href={src} target="_blank" rel="noreferrer noopener">
      <img src={src} alt="" loading="lazy" decoding="async" onLoad={() => setState('loaded')} onError={() => setState('failed')} />
    </a>
  );
}

function EmbedCard({ embed }: { embed: PostEmbedValue }) {
  if (embed.type === PostEmbed.typePermalink) {
    return (
      <a className="attachment-card" href={embed.url || '#'} target="_blank" rel="noreferrer noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className="rule" style={{ background: 'var(--accent)' }} />
        <div className="body">
          <div className="field-title">{embed.channelDisplayName}</div>
          {embed.description ? <div style={{ color: 'var(--text-secondary)' }}>{embed.description}</div> : null}
        </div>
      </a>
    );
  }
  return (
    <LinkPreviewCard
      href={embed.url}
      site={embed.siteName}
      title={embed.title}
      description={embed.description}
      imageSrc={PostEmbed.imageSrc(embed.imageUrl, mediaBase())}
      aspect={PostEmbed.imageAspect(embed)}
    />
  );
}

/**
 * A picture link (§40.1, Mattermost `image` embed): the picture-only preview card — the link's own
 * image through the same-origin image proxy (never the third-party host), OG image box rules, the
 * host as the site line; tap opens the link.
 */
function PicturePreviewCard({ embed }: { embed: PostEmbedValue }) {
  return (
    <LinkPreviewCard
      href={embed.url}
      site={embed.siteName}
      title=""
      description=""
      imageSrc={CoreLinkCards.proxiedImage(embed.imageUrl || embed.url, mediaBase())}
      aspect={PostEmbed.imageAspect(embed)}
      kind="picture"
    />
  );
}

/**
 * Core's plain web preview (§40.1 `kind: "web"`): the SAME OpenGraph card, plus the site's 16 px icon
 * (the link glyph when there is none or it fails). Image and icon load only through the server's image
 * proxy on this origin — never from the third-party host.
 */
function WebPreviewCard({ card, href }: { card: WebCard; href: string }) {
  const base = mediaBase();
  return (
    <LinkPreviewCard
      href={card.url || href}
      site={card.site}
      iconSrc={CoreLinkCards.proxiedImage(card.icon, base)}
      title={card.title}
      description={card.description ?? ''}
      imageSrc={CoreLinkCards.proxiedImage(card.image, base)}
      aspect={1.91}
      kind="web"
    />
  );
}

/** The site's icon on the eyebrow line; null or a failed load → the link glyph. */
function SiteIcon({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <Icon name="link" size={16} className="og-site-icon glyph" />;
  return <img className="og-site-icon" src={src} alt="" width={16} height={16} loading="lazy" decoding="async" onError={() => setFailed(true)} />;
}

/**
 * The OpenGraph preview card (§12.4), shared by Mattermost's embeds and Core's web previews (§40.1):
 * site line, bold title (2 lines), description (2 lines), the image across the top — its box reserved
 * before the bytes arrive (measured, else 1.91:1), ≤ 180 px, cover-cropped; a failed load collapses
 * it and the text card stays. `iconSrc` undefined = no icon slot (Mattermost's embeds carry none).
 */
function LinkPreviewCard(p: {
  href: string;
  site: string;
  iconSrc?: string | null;
  title: string;
  description: string;
  imageSrc: string | null;
  aspect: number;
  kind?: string;
}) {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  useEffect(() => setImageState('loading'), [p.imageSrc]);
  const text = (
    <>
      <div className="rule" style={{ background: 'var(--accent)' }} />
      <div className="body">
        <div className="field-title og-site">
          {p.iconSrc !== undefined ? <SiteIcon src={p.iconSrc} /> : null}
          <span className="og-site-name">{p.site}</span>
        </div>
        {p.title ? <div className="title og-title">{p.title}</div> : null}
        {p.description ? <div className="og-description">{p.description}</div> : null}
      </div>
    </>
  );
  const linkProps = { href: p.href || '#', target: '_blank', rel: 'noreferrer noopener', style: { textDecoration: 'none', color: 'inherit' }, 'data-preview-kind': p.kind };
  if (!p.imageSrc) {
    return (
      <a className="attachment-card og-text-card" {...linkProps}>
        {text}
      </a>
    );
  }
  return (
    <a className="attachment-card og-card" {...linkProps}>
      <span className={`og-image ${imageState}`} style={{ aspectRatio: String(p.aspect) }}>
        <img src={p.imageSrc} alt="" loading="lazy" decoding="async" onLoad={() => setImageState('loaded')} onError={() => setImageState('failed')} />
      </span>
      <div className="og-row">{text}</div>
    </a>
  );
}

/**
 * The message's preview slot (§40 + §40.1). Its first Core link (outside code) gets Core's card —
 * a live record card or a `kind: "page"` card — which REPLACES the OpenGraph / plain preview. Any
 * other link that Mattermost's embed does not preview gets Core's plain web preview, drawn as the
 * OpenGraph card. Until Core answers (or when it has nothing) the usual preview shows — nothing extra
 * is drawn or reserved while the first answer is pending.
 */
function PreviewSlot({ message, embed, suppressed, fallback }: { message: string; embed: PostEmbedValue | null; suppressed: boolean; fallback: React.ReactNode }) {
  const serverPreview = PostEmbed.hasUsablePreview(embed);
  const webPreviews = linkPreviewsEnabled();
  const url = useMemo(() => CoreLinkCards.previewUrl(message, { serverPreview, suppressed, webPreviews }), [message, serverPreview, suppressed, webPreviews]);
  if (!url) return <>{fallback}</>;
  return <CorePreview url={url} fallback={fallback} />;
}

function CorePreview({ url, fallback }: { url: string; fallback: React.ReactNode }) {
  const { card, ref } = useCoreCard(url);
  return (
    <>
      <span ref={ref} className="core-card-anchor" aria-hidden />
      {!card ? fallback : CoreLinkCards.isWebCard(card) ? <WebPreviewCard card={card} href={url} /> : <CoreCardView card={card} href={url} />}
    </>
  );
}
